import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NewsletterTable = ({ newsletters, onEdit, onDelete, onPublish, onView }) => {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString)?.toLocaleDateString('en-US', options);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { color: 'bg-success/10 text-success border-success/20', label: 'Published' },
      draft: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Draft' },
      scheduled: { color: 'bg-secondary/10 text-secondary border-secondary/20', label: 'Scheduled' }
    };
    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`px-2.5 py-1 text-xs font-medium border rounded-full ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedNewsletters = [...newsletters]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];
    
    if (sortField === 'date') {
      aVal = new Date(aVal)?.getTime();
      bVal = new Date(bVal)?.getTime();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Title
                  <Icon name={sortField === 'title' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={16} />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Status
                  <Icon name={sortField === 'status' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={16} />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Date
                  <Icon name={sortField === 'date' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={16} />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <span className="text-sm font-semibold text-foreground">Engagement</span>
              </th>
              <th className="px-6 py-4 text-right">
                <span className="text-sm font-semibold text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedNewsletters?.map((newsletter) => (
              <tr key={newsletter?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium text-foreground line-clamp-1">{newsletter?.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1">{newsletter?.summary}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(newsletter?.status)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Calendar" size={14} />
                    <span>{formatDate(newsletter?.date)}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Icon name="Eye" size={14} />
                      <span>{newsletter?.views}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Icon name="MousePointerClick" size={14} />
                      <span>{newsletter?.clicks}</span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onView(newsletter?.id)}
                      iconName="Eye"
                      iconSize={14}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(newsletter?.id)}
                      iconName="Edit"
                      iconSize={14}
                      className="text-muted-foreground hover:text-primary"
                    >
                      Edit
                    </Button>
                    {newsletter?.status === 'draft' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onPublish(newsletter?.id)}
                        iconName="Send"
                        iconSize={14}
                        className="text-success hover:text-success/80"
                      >
                        Publish
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(newsletter?.id)}
                      iconName="Trash2"
                      iconSize={14}
                      className="text-error hover:text-error/80"
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NewsletterTable;